import Button from "@/common/elements/Button";
import TabsV2 from "@/common/elements/TabsV2";
import {
  ActionHeader,
  InmateDetailsContainer,
  PageTabsWrapper,
  StyledActionControlContainer,
} from "@/common/styles";
import Personal, { PERSON } from "@/components/InmateDetails/Personal";

import { useEffect, useImperativeHandle, useMemo, useState } from "react";
import { CreateStaffingContainer, StyleadInnerHeader } from "./styles";
import AssignmentLog from "./tabs/AssignmentLog";
import Equipments from "./tabs/Equipments.jsx";
import GroupsSecurityRules from "./tabs/GroupsSecurityRules.jsx";
import LoginInfo from "./tabs/LoginInfo";
import MedicalInfo from "./tabs/MedicalInfo";
import SkillsCertifications from "./tabs/SkillsCertifications.jsx";
import Supplementals from "./tabs/Supplementals.jsx";
import PersonalInformationDetails from "@/components/BookingsDetails/PersonalInformationDetails";
import Docs from "@/components/InmateDetails/Docs";
import SupplementalDetails from "@/components/BookingsDetails/OfficerReports/SupplementalDetails";
import StaffAdministrativeInformation from "../Users/sections/StaffAdministrativeInformation";
import StaffPoliceCentralUserInfo from "./tabs/StaffPoliceCentralUserInfo";
import TrainingClassDetails from "./tabs/TrainingClassDetails";
import OrganizationalRank from "./tabs/OrganizationalRank";
import { MODULES_LIST } from "@/util/common_variable";
import { hasCheckSecurity } from "@/util/common_function";
import { Arbitrary_Names, Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";

export const MODE = { NEW: "new", EDIT: "edit" };

const CreateStaffing = ({
  allFormRef,
  onNextClickTrigger,
  nextBtnRef,
  tabChangeRef,
  module = MODULES_LIST.STAFFING,
  startingPoint,
}) => {
  const showLoginInfo = hasCheckSecurity(
                          Security_Types.functionality,
                          Entity_types.arbitary,
                          LABELS_CAPITAL.VIEW,
                          Arbitrary_Names.EditLogin
                        );
  const [currentTabKey, setCurrentTabKey] = useState(null);
  const sectionItems = useMemo(() => {
    if (module === MODULES_LIST.USERS) {
      const items = [
        {
          id: 1,
          key: "personal-information",
          label: "Personal Information",
          children: (
            <PersonalInformationDetails
              allFormRef={allFormRef}
              isStaff={true}
              detailLevel={PERSON.LOW}
              startingPoint={startingPoint}
            />
          ),
        },
        {
          id: 5,
          key: "organizational",
          label: "Organizational Rank",
          children: (
            <OrganizationalRank
              organizationRankRef={allFormRef?.organizationRankRef}
              startingPoint={startingPoint}
            />
          ),
        },
      ];

      // ✅ conditional tab
      if (showLoginInfo) {
        items.push({
          id: 12,
          key: "police-central-userinfo",
          label: "Login information",
          children: (
            <StaffPoliceCentralUserInfo
              policeCentralUserRef={allFormRef?.policeCentralUserRef}
              startingPoint={startingPoint}
            />
          ),
        });
      }

      items.push({
        id: 13,
        key: "staff-administrative-information",
        label: "Staff Administrative Information",
        children: (
          <StaffAdministrativeInformation startingPoint={startingPoint} />
        ),
      });

      return items;
    }

    const items = [
      {
        id: 1,
        key: "personal-information",
        label: "Personal Information",
        children: (
          <PersonalInformationDetails
            allFormRef={allFormRef}
            isStaff={true}
            detailLevel={PERSON.STAFF}
            startingPoint={startingPoint}
          />
        ),
      },
      {
        id: 3,
        key: "medical",
        label: "Medical Information",
        children: <MedicalInfo startingPoint={startingPoint}/>,
      },
      {
        id: 4,
        key: "assignment",
        label: "Assignment/Activity Log",
        children: <AssignmentLog startingPoint={startingPoint}/>,
      },
      {
        id: 5,
        key: "organizational",
        label: "Organizational Rank",
        children: (
          <OrganizationalRank
            organizationRankRef={allFormRef?.organizationRankRef}
            startingPoint={startingPoint}
          />
        ),
      },
      {
        id: 6,
        key: "skills",
        label: "Skills/Certifications",
        children: <SkillsCertifications startingPoint={startingPoint}/>,
      },
      {
        id: 7,
        key: "training",
        label: "Training",
        children: <TrainingClassDetails startingPoint={startingPoint}/>,
      },
      {
        id: 8,
        key: "equipments",
        label: "Assigned Equipment",
        children: <Equipments startingPoint={startingPoint}/>,
      },
      {
        id: 9,
        key: "linked-scanned-documents",
        label: "Linked and Scanned Documents",
        children: <Docs parentKey={MODULES_LIST.STAFF} startingPoint={startingPoint}/>,
      },
      {
        id: 10,
        key: "supplementals",
        label: "Staff Supplementals",
        children: <SupplementalDetails showAgencyInfo={true} startingPoint={startingPoint}/>,
      },
    ];

    if (showLoginInfo) {
      items.push({
        id: 12,
        key: "police-central-userinfo",
        label: "Login information",
        children: (
          <StaffPoliceCentralUserInfo
            policeCentralUserRef={allFormRef?.policeCentralUserRef}
            startingPoint={startingPoint}
          />
        ),
      });
    }

    items.push(
      {
        id: 13,
        key: "staff-administrative-information",
        label: "Staff Administrative Information",
        children: <StaffAdministrativeInformation startingPoint={startingPoint}/>,
      }
    );
    return items;
  }, [module, showLoginInfo, allFormRef, startingPoint]);

  useImperativeHandle(tabChangeRef, () => ({
    onTabChange: onTabChange,
  }));

  const onTabChange = (value) => {
    setCurrentTabKey(value);
  };

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("from-jacket");
    };
  }, []);

  useEffect(() => {
    if (!showLoginInfo && currentTabKey === "police-central-userinfo") {
      setCurrentTabKey("personal-information");
    }
  }, [showLoginInfo, currentTabKey]);

  return (
    <CreateStaffingContainer>
      <InmateDetailsContainer>
        <PageTabsWrapper style={{ height: "100%" }}>
          <TabsV2
            navButtonsColor="default"
            items={sectionItems}
            activeKey={currentTabKey}
            onChange={onTabChange}
            nextBtnRef={nextBtnRef}
            onNextClickTrigger={onNextClickTrigger}
          />
        </PageTabsWrapper>
      </InmateDetailsContainer>
    </CreateStaffingContainer>
  );
};

export default CreateStaffing;
